/* Size finder quiz — reads data attributes rendered by the size_finder block
   in sections/main-product.liquid. Zero network requests: recommendations come
   only from the product's own variant values passed via data-sizes, and variant
   selection happens by checking the existing picker input and dispatching a
   bubbling 'change' event. */
(function () {
  'use strict';

  var EPS = 0.001;

  var CHAIN_TARGETS = {
    him: { snug: 18, collar: 20, chest: 22, long: 24, xlong: 28 },
    her: { snug: 16, collar: 18, chest: 20, long: 24, xlong: 26 },
    gift: { snug: 18, collar: 19, chest: 21, long: 24, xlong: 27 },
  };

  var SIT_REASONS = {
    snug: 'a snug fit at the neck',
    collar: 'a fit right at the collarbone',
    chest: 'a drop onto the chest',
    long: 'a long drop over clothing',
    xlong: 'a statement drop over layers',
  };

  /* When the final recommendation is the largest size the piece comes in,
     this copy replaces the whole reason line. */
  var MAX_SIZE_REASONS = {
    chain: 'The longest we make — full statement length.',
    bracelet: 'The roomiest fit we make.',
    anklet: 'The roomiest fit we make.',
  };

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function fmt(n) {
    return String(Math.round(n * 100) / 100);
  }

  /* Strip straight/curly quotes and inch words, then parseFloat. */
  function cleanFloat(raw) {
    if (raw == null) return NaN;
    var s = String(raw)
      .replace(/["“”]/g, ' ')
      .replace(/inches|inch|in/gi, ' ');
    return parseFloat(s.trim());
  }

  function parseSizes(raw) {
    var out = [];
    String(raw || '')
      .split(',')
      .forEach(function (part) {
        var v = cleanFloat(part);
        if (isFinite(v) && out.indexOf(v) === -1) out.push(v);
      });
    out.sort(function (a, b) {
      return a - b;
    });
    return out;
  }

  /* Nearest available size; ties round UP. Expects sizes sorted ascending. */
  function nearest(sizes, target) {
    var best = sizes[0];
    var bestD = Math.abs(sizes[0] - target);
    for (var i = 1; i < sizes.length; i++) {
      var d = Math.abs(sizes[i] - target);
      if (d < bestD - EPS || (Math.abs(d - bestD) <= EPS && sizes[i] > best)) {
        best = sizes[i];
        bestD = d;
      }
    }
    return best;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function pillRow(options, onPick) {
    var row = el('div', 'sf-pills');
    options.forEach(function (opt) {
      var pill = el('button', 'sf-pill', opt.label);
      pill.type = 'button';
      pill.setAttribute('aria-pressed', 'false');
      pill.addEventListener('click', function () {
        var pills = row.querySelectorAll('.sf-pill');
        for (var i = 0; i < pills.length; i++) {
          pills[i].classList.remove('is-selected');
          pills[i].setAttribute('aria-pressed', 'false');
        }
        pill.classList.add('is-selected');
        pill.setAttribute('aria-pressed', 'true');
        onPick(opt.key);
      });
      row.appendChild(pill);
    });
    return row;
  }

  function setup(root) {
    if (root.hasAttribute('data-sf-ready')) return;
    root.setAttribute('data-sf-ready', '');

    var cfg = {
      mode: root.getAttribute('data-mode') || '',
      sizes: parseSizes(root.getAttribute('data-sizes')),
      optionIndex: parseInt(root.getAttribute('data-option-index'), 10),
      optionName: root.getAttribute('data-option-name') || '',
      sectionId: root.getAttribute('data-section') || '',
      min: cleanFloat(root.getAttribute('data-min')) || 0,
      max: cleanFloat(root.getAttribute('data-max')) || 0,
      eyebrow: root.getAttribute('data-result-eyebrow') || 'We recommend',
      selectLabel: root.getAttribute('data-select-label') || 'Select this size',
    };
    if (!cfg.sizes.length) {
      /* No numeric sizes parsed (e.g. "One size", "Adjustable"): the quiz can
         never render a result, so remove the finder's own accordion wrapper. */
      var detailsEl = root.closest('details');
      var accordionEl = detailsEl ? detailsEl.closest('.product__accordion') : null;
      var removeTarget = accordionEl || detailsEl || root;
      if (removeTarget.parentNode) removeTarget.parentNode.removeChild(removeTarget);
      return;
    }
    if (!isFinite(cfg.optionIndex)) return;

    var stepsWrap = el('div', 'sf-steps');
    var resultWrap = el('div', 'sf-result-wrap');
    /* role=status implies polite live-region semantics, so a rendered or
       re-rendered recommendation is announced without stealing focus. */
    resultWrap.setAttribute('role', 'status');
    root.appendChild(stepsWrap);
    root.appendChild(resultWrap);
    var steps = [];

    /* The most recent Select button and the size it committed; used by the
       stale-check listener below to clear an outdated "selected ✓". */
    var currentBtn = null;
    var currentSize = NaN;

    function reveal(node) {
      if (reducedMotion()) {
        node.classList.add('sf-in');
      } else {
        window.requestAnimationFrame(function () {
          node.classList.add('sf-in');
        });
      }
    }

    function addStep(labelText, contentNode) {
      var step = el('div', 'sf-step');
      step.appendChild(el('p', 'sf-q', labelText));
      step.appendChild(contentNode);
      stepsWrap.appendChild(step);
      steps.push(step);
      reveal(step);
      return step;
    }

    function clearResult() {
      while (resultWrap.firstChild) resultWrap.removeChild(resultWrap.firstChild);
    }

    /* Re-answering an earlier question removes every later step and the result. */
    function truncateAfter(step) {
      var idx = steps.indexOf(step);
      while (steps.length > idx + 1) stepsWrap.removeChild(steps.pop());
      clearResult();
    }

    function xlongAllowed() {
      var maxAvail = cfg.sizes[cfg.sizes.length - 1];
      return maxAvail >= 26 && (cfg.max === 0 || cfg.max >= 26);
    }

    /* Locate the existing picker control for the size option. Resolved fresh on
       every call because Dawn re-renders the variant picker on variant change.
       Scoped to this section's own picker; the constructed id is a fallback.
       Rendered input names carry trailing whitespace from Dawn's Liquid capture
       (e.g. "Length-1\n"), so names are compared trimmed. */
    function findControl(size) {
      var section = root.closest('.shopify-section');
      var picker = section ? section.querySelector('variant-selects') : null;
      if (!picker) picker = document.getElementById('variant-selects-' + cfg.sectionId);
      if (!picker) return null;
      var radioName = cfg.optionName + '-' + (cfg.optionIndex + 1);
      var selectName = 'options[' + cfg.optionName + ']';
      var radios = picker.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        var v = cleanFloat(radios[i].value);
        if (radios[i].name.trim() === radioName && isFinite(v) && Math.abs(v - size) < EPS) {
          return { type: 'radio', node: radios[i] };
        }
      }
      var selects = picker.querySelectorAll('select');
      for (var j = 0; j < selects.length; j++) {
        if (selects[j].name.trim() !== selectName) continue;
        for (var k = 0; k < selects[j].options.length; k++) {
          var sv = cleanFloat(selects[j].options[k].value);
          if (isFinite(sv) && Math.abs(sv - size) < EPS) {
            return { type: 'select', node: selects[j], value: selects[j].options[k].value };
          }
        }
      }
      return null;
    }

    /* Sold-out test for a resolved control. Button-picker radios mark
       unavailable values with class="disabled" (input left enabled); the
       swatch picker's radios use "visually-disabled" instead, so both are
       checked. Dropdown pickers signal unavailability only via option label
       text (no class/attribute), so select controls always test available
       here — conservative: no sold-out claims we cannot verify. */
    function isSoldOut(control) {
      if (!control) return false;
      if (control.type === 'radio') {
        return (
          control.node.disabled ||
          control.node.classList.contains('disabled') ||
          control.node.classList.contains('visually-disabled')
        );
      }
      return false;
    }

    /* Nearest size whose picker control exists and is in stock; ties round
       up, matching nearest(). Returns null when nothing is available. */
    function nearestAvailable(target) {
      var best = null;
      var bestD = Infinity;
      for (var i = 0; i < cfg.sizes.length; i++) {
        var s = cfg.sizes[i];
        var c = findControl(s);
        if (!c || isSoldOut(c)) continue;
        var d = Math.abs(s - target);
        if (d < bestD - EPS || (Math.abs(d - bestD) <= EPS && (best === null || s > best))) {
          best = s;
          bestD = d;
        }
      }
      return best;
    }

    function showResult(ideal, reason, isXlong) {
      clearResult();
      var clamped = ideal;
      if (cfg.min > 0 && clamped < cfg.min) clamped = cfg.min;
      if (cfg.max > 0 && clamped > cfg.max) clamped = cfg.max;
      var size = nearest(cfg.sizes, clamped);
      var largest = cfg.sizes[cfg.sizes.length - 1];
      /* Max-size copy only when genuinely capped: the pick landed on the
         largest stocked size AND the unclamped ideal exceeded it (or, for
         chains, the statement tier was chosen — its max IS full length).
         A merchant max clamp below the largest stocked size never matches. */
      var wasCapped =
        Math.abs(size - largest) < EPS &&
        (ideal > largest + EPS || (cfg.mode === 'chain' && isXlong === true));
      if (wasCapped) {
        reason = MAX_SIZE_REASONS[cfg.mode] || reason;
      }

      var card = el('div', 'sf-card');
      card.appendChild(el('p', 'sf-eyebrow', cfg.eyebrow));
      card.appendChild(el('p', 'sf-size', fmt(size) + '"'));
      card.appendChild(el('p', 'sf-reason', reason));
      if (Math.abs(size - ideal) > EPS) {
        card.appendChild(
          el(
            'p',
            'sf-note',
            'Ideal is ' + fmt(ideal) + '″ — ' + fmt(size) + '″ is the closest size this piece comes in.'
          )
        );
      }

      /* Availability: if the recommended size's control is sold out, keep the
         ideal recommendation figure but say so, offer the nearest in-stock
         size when one exists, and point the Select button at it. A null
         control (picker not found) keeps current behavior: no button, no
         sold-out claims. */
      var selectSize = size;
      var recommendedControl = findControl(size);
      if (recommendedControl && isSoldOut(recommendedControl)) {
        card.appendChild(el('p', 'sf-note', 'This length is currently sold out'));
        var alt = nearestAvailable(size);
        if (alt !== null) {
          card.appendChild(el('p', 'sf-note', 'Closest in-stock: ' + fmt(alt) + '"'));
          selectSize = alt;
        }
      }

      if (recommendedControl) {
        var btn = el('button', 'sf-select', cfg.selectLabel);
        btn.type = 'button';
        btn.addEventListener('click', function () {
          var control = findControl(selectSize);
          if (!control) return;
          if (control.type === 'radio') {
            control.node.checked = true;
            control.node.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            control.node.value = control.value;
            control.node.dispatchEvent(new Event('change', { bubbles: true }));
          }
          btn.textContent = fmt(selectSize) + '" selected ✓';
          btn.classList.add('is-done');
          currentBtn = btn;
          currentSize = selectSize;
        });
        card.appendChild(btn);
      }

      resultWrap.appendChild(card);
      /* Announce + focus: land keyboard/AT focus on the new card without
         yanking the scroll position, then bring it gently into view. */
      card.setAttribute('tabindex', '-1');
      card.focus({ preventScroll: true });
      card.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      reveal(card);
    }

    function askMeasure(labelText, lo, hi, onSubmit) {
      var wrap = el('div', 'sf-measure');
      var input = el('input', 'sf-input');
      input.type = 'number';
      input.min = String(lo);
      input.max = String(hi);
      input.step = '0.25';
      input.inputMode = 'decimal';
      input.setAttribute('aria-label', labelText);
      var go = el('button', 'sf-go', 'Get my size');
      go.type = 'button';
      go.addEventListener('click', function () {
        var v = parseFloat(input.value);
        if (!isFinite(v)) return;
        if (v < lo) v = lo;
        if (v > hi) v = hi;
        onSubmit(v);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          go.click();
        }
      });
      wrap.appendChild(input);
      wrap.appendChild(go);
      addStep(labelText, wrap);
    }

    function frameReason(frameKey, suffix) {
      var article = frameKey === 'average' ? 'an average' : 'a ' + frameKey;
      return 'Estimated from ' + article + ' frame' + (suffix || '') + '.';
    }

    function startChain() {
      var who = null;
      var sit = null;
      var q1 = addStep(
        'Who is it for?',
        pillRow(
          [
            { key: 'him', label: 'Him' },
            { key: 'her', label: 'Her' },
            { key: 'gift', label: "It's a gift — not sure" },
          ],
          function (key) {
            who = key;
            truncateAfter(q1);
            askSit();
          }
        )
      );

      function askSit() {
        var opts = [
          { key: 'snug', label: 'Snug at the neck' },
          { key: 'collar', label: 'At the collarbone' },
          { key: 'chest', label: 'On the chest' },
          { key: 'long', label: 'Long, over clothing' },
        ];
        if (xlongAllowed()) opts.push({ key: 'xlong', label: 'Statement drop, over a hoodie' });
        var q2 = addStep(
          'Where should it sit?',
          pillRow(opts, function (key) {
            sit = key;
            truncateAfter(q2);
            askBuild();
          })
        );
      }

      function askBuild() {
        var q3 = addStep(
          'Their build?',
          pillRow(
            [
              { key: 'slim', label: 'Slim' },
              { key: 'average', label: 'Average' },
              { key: 'broad', label: 'Broad' },
            ],
            function (key) {
              truncateAfter(q3);
              var ideal = CHAIN_TARGETS[who][sit];
              if (key === 'broad') ideal += 2;
              var reason = 'Sized for ' + SIT_REASONS[sit];
              if (who === 'gift') reason += ', with room to gift safely';
              else reason += ' for ' + who;
              if (key === 'broad') reason += ', adjusted up for a broader build';
              showResult(ideal, reason + '.', sit === 'xlong');
            }
          )
        );
      }
    }

    function startBracelet() {
      var q1 = addStep(
        'Do you know the wrist size?',
        pillRow(
          [
            { key: 'yes', label: 'Yes' },
            { key: 'no', label: 'No' },
          ],
          function (key) {
            truncateAfter(q1);
            if (key === 'yes') {
              askMeasure('Wrist size (inches)', 4, 10, function (v) {
                showResult(v + 0.5, 'Based on a ' + fmt(v) + '″ wrist plus ½″ of comfort room.');
              });
            } else {
              askWho();
            }
          }
        )
      );

      function askWho() {
        var q2 = addStep(
          'Who is it for?',
          pillRow(
            [
              { key: 'him', label: 'Him' },
              { key: 'her', label: 'Her' },
            ],
            function (who) {
              truncateAfter(q2);
              askFrame(who);
            }
          )
        );
      }

      function askFrame(who) {
        var q3 = addStep(
          'Their frame?',
          pillRow(
            [
              { key: 'small', label: 'Small' },
              { key: 'average', label: 'Average' },
              { key: 'large', label: 'Large' },
            ],
            function (frame) {
              truncateAfter(q3);
              var ideal = who === 'him' ? 8 : 7;
              if (frame === 'small') ideal -= 0.5;
              if (frame === 'large') ideal += 0.5;
              showResult(ideal, frameReason(frame, ' for ' + who));
            }
          )
        );
      }
    }

    function startAnklet() {
      var q1 = addStep(
        'Do you know the ankle size?',
        pillRow(
          [
            { key: 'yes', label: 'Yes' },
            { key: 'no', label: 'No' },
          ],
          function (key) {
            truncateAfter(q1);
            if (key === 'yes') {
              askMeasure('Ankle size (inches)', 7, 13, function (v) {
                showResult(v + 0.5, 'Based on a ' + fmt(v) + '″ ankle plus ½″ of comfort room.');
              });
            } else {
              var q2 = addStep(
                'Their frame?',
                pillRow(
                  [
                    { key: 'small', label: 'Small' },
                    { key: 'average', label: 'Average' },
                    { key: 'large', label: 'Large' },
                  ],
                  function (frame) {
                    truncateAfter(q2);
                    var ideal = frame === 'small' ? 9 : frame === 'average' ? 9.5 : 10;
                    showResult(ideal, frameReason(frame));
                  }
                )
              );
            }
          }
        )
      );
    }

    /* Stale ✓ reset: one listener per finder — setup() is already guarded by
       data-sf-ready, so this can never attach twice for the same root.
       Attached to the section rather than variant-selects because Dawn
       replaces the picker element on re-render; change events bubble here. */
    var changeScope = root.closest('.shopify-section') || document;
    changeScope.addEventListener('change', function (e) {
      if (!currentBtn || !currentBtn.classList.contains('is-done')) return;
      var t = e.target;
      if (!t || !t.closest || !t.closest('variant-selects')) return;
      var v = cleanFloat(t.value);
      if (!isFinite(v) || Math.abs(v - currentSize) > EPS) {
        currentBtn.textContent = cfg.selectLabel;
        currentBtn.classList.remove('is-done');
      }
    });

    if (cfg.mode === 'chain') startChain();
    else if (cfg.mode === 'bracelet') startBracelet();
    else if (cfg.mode === 'anklet') startAnklet();
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-size-finder]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }
  /* Quick-add re-executes injected scripts; guard so only the first execution
     installs the persistent section:load listener. Marker set before install
     so a mid-sequence throw can't leave a half-armed state. */
  if (!document.documentElement.hasAttribute('data-sf-section-listener')) {
    document.documentElement.setAttribute('data-sf-section-listener', '');
    document.addEventListener('shopify:section:load', function (e) {
      initAll(e.target);
    });
  }
})();
